/**
 * referral.js — Referral system library for Omnisio Workout
 *
 * Flow:
 *  1. On app mount, captureReferral() reads ?ref= from the URL and stores it.
 *  2. When a NEW user completes onboarding, App.jsx calls applyReferralReward()
 *     with the referrer's public ID.
 *  3. applyReferralReward() is a client-side stub that writes premium status to
 *     localStorage. The backend (Supabase Edge Function or server route) is
 *     responsible for the real reward once the user syncs.
 *
 * All localStorage keys are prefixed with `omnisio_workout_` per project convention.
 */

import { isValidPublicId } from './userId';

// ─── Storage key constants ────────────────────────────────────────────────────
const KEY_REFERRER        = 'omnisio_workout_referrer';          // { code, capturedAt }
const KEY_REFERRED_BY     = 'omnisio_workout_referred_by';       // { code, appliedAt }
const KEY_PREMIUM_UNTIL   = 'omnisio_workout_premium_until';     // ISO date string

/** Base URL for referral join links */
const REFERRAL_BASE_URL = 'https://omnisio.app/workout/join';

/** How long referral premium lasts (ms) */
const PREMIUM_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** How long a captured referral code is valid before expiry (ms) */
const CAPTURE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Generate a referral code for a user.
 * The referral code IS the public ID — no extra encoding needed.
 *
 * @param {string} publicId  e.g. "OMW-AB3M7Q"
 * @returns {string}  The referral code (same as publicId)
 */
export function generateReferralCode(publicId) {
  if (!isValidPublicId(publicId)) {
    throw new Error(`[REFERRAL] Invalid publicId: ${publicId}`);
  }
  return publicId;
}

/**
 * Generate a full referral link for sharing.
 *
 * @param {string} publicId  e.g. "OMW-AB3M7Q"
 * @param {string} [lang='en']  BCP-47 language tag for the landing page
 * @returns {string}  e.g. "https://omnisio.app/workout/join?ref=OMW-AB3M7Q&lang=en"
 */
export function generateReferralLink(publicId, lang = 'en') {
  const code = generateReferralCode(publicId); // validates
  const params = new URLSearchParams({ ref: code, lang });
  return `${REFERRAL_BASE_URL}?${params.toString()}`;
}

/**
 * Read the ?ref= query param from the current page URL, validate it, and
 * store it in localStorage with a timestamp for expiry enforcement.
 *
 * Should be called ONCE on app mount, before auth resolves.
 *
 * @returns {string|null}  The captured referral code, or null if none found / invalid
 */
export function captureReferral() {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');

    if (!ref) return null;

    if (!isValidPublicId(ref)) {
      console.warn('[REFERRAL] Ignored invalid ?ref= value:', ref);
      return null;
    }

    const payload = {
      code: ref,
      capturedAt: new Date().toISOString(),
    };

    localStorage.setItem(KEY_REFERRER, JSON.stringify(payload));
    console.log('[REFERRAL] Captured referral code:', ref);
    return ref;
  } catch (err) {
    console.error('[REFERRAL] captureReferral error:', err);
    return null;
  }
}

/**
 * Retrieve the stored referrer code if it exists and hasn't expired.
 *
 * @returns {string|null}  Referral code or null
 */
export function getStoredReferrer() {
  try {
    const raw = localStorage.getItem(KEY_REFERRER);
    if (!raw) return null;

    const { code, capturedAt } = JSON.parse(raw);

    // Expire old captures (7 days)
    if (capturedAt) {
      const age = Date.now() - new Date(capturedAt).getTime();
      if (age > CAPTURE_EXPIRY_MS) {
        localStorage.removeItem(KEY_REFERRER);
        console.log('[REFERRAL] Stored referrer expired, cleared.');
        return null;
      }
    }

    return isValidPublicId(code) ? code : null;
  } catch (err) {
    console.error('[REFERRAL] getStoredReferrer error:', err);
    return null;
  }
}

/**
 * Clear the stored referrer code.
 * Call this AFTER the referral reward has been processed on the server,
 * so the user doesn't get credited twice.
 */
export function clearReferrer() {
  try {
    localStorage.removeItem(KEY_REFERRER);
  } catch (err) {
    console.error('[REFERRAL] clearReferrer error:', err);
  }
}

/**
 * Apply a referral reward for the current user (CLIENT-SIDE STUB).
 *
 * This writes premium status to localStorage immediately so the user sees
 * the benefit right away. The server must confirm and persist this via a
 * Supabase Edge Function or API route when connectivity is available.
 *
 * Guards against:
 *  - Self-referral (referrerId === currentUserPublicId)
 *  - Double-apply (omnisio_workout_referred_by already set)
 *
 * @param {string} referrerId       - The referrer's public ID (e.g. "OMW-AB3M7Q")
 * @param {string} [currentUserPublicId]  - Optional: the current user's public ID for
 *                                          self-referral detection
 * @returns {{ success: boolean, premiumUntil?: string, reason?: string }}
 */
export function applyReferralReward(referrerId, currentUserPublicId = null) {
  try {
    // 1. Validate the referrer ID
    if (!isValidPublicId(referrerId)) {
      return { success: false, reason: 'invalid_referrer_id' };
    }

    // 2. Self-referral check
    if (currentUserPublicId && referrerId === currentUserPublicId) {
      console.warn('[REFERRAL] Self-referral attempt blocked:', referrerId);
      return { success: false, reason: 'self_referral' };
    }

    // 3. Idempotency check — already referred
    const existingReferral = localStorage.getItem(KEY_REFERRED_BY);
    if (existingReferral) {
      try {
        const { code } = JSON.parse(existingReferral);
        console.log('[REFERRAL] Already referred by:', code);
      } catch { /* ignore parse errors */ }
      return { success: false, reason: 'already_referred' };
    }

    // 4. Apply reward
    const now = new Date();
    const premiumUntil = new Date(now.getTime() + PREMIUM_DURATION_MS).toISOString();

    // Record who referred this user
    localStorage.setItem(KEY_REFERRED_BY, JSON.stringify({
      code: referrerId,
      appliedAt: now.toISOString(),
    }));

    // Grant 30 days of premium access
    localStorage.setItem(KEY_PREMIUM_UNTIL, premiumUntil);

    console.log('[REFERRAL] Reward applied. Premium until:', premiumUntil);

    // Clear the pending referrer entry — reward is now processed
    clearReferrer();

    return { success: true, premiumUntil };
  } catch (err) {
    console.error('[REFERRAL] applyReferralReward error:', err);
    return { success: false, reason: 'internal_error' };
  }
}

/**
 * Check whether the current user has active referral-granted premium.
 * Useful for display logic without requiring a server round-trip.
 *
 * @returns {{ active: boolean, until: string|null }}
 */
export function getReferralPremiumStatus() {
  try {
    const until = localStorage.getItem(KEY_PREMIUM_UNTIL);
    if (!until) return { active: false, until: null };
    const active = new Date(until).getTime() > Date.now();
    return { active, until };
  } catch {
    return { active: false, until: null };
  }
}
